import { DomainDto } from '../../../dtos/domain'
import { JourneyBuilderMode } from '../enums/journey-builder-mode.enum'
import * as React from 'react'
import { Dispatch } from 'react'
import { Journey } from './journey-api-response'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { AddStepType, JourneyEdge, JourneyNode, SendActionStep } from './journey-nodes'
import type { IJourneyStep, IActionStep, IDelayStep } from '@pushly/models/lib/structs/journeys'

// region JourneyContext

export type JourneyProps = {
    domain: DomainDto
    journey: Journey
    mode: JourneyBuilderMode
    nodes: JourneyNode[]
    edges: JourneyEdge[]
    copy: CopyState
    editorOpen: boolean
    activeNode?: JourneyNode
}

export enum CopyStates {
    CLEAR = 'clear',
    FILLED = 'filled',
}

export enum CopyTypes {
    COPY,
    CUT,
}

type CopyState =
    | {
          state: CopyStates.CLEAR
          node: null
      }
    | {
          state: CopyStates.FILLED
          type: CopyTypes.CUT | CopyTypes.COPY
          node: JourneyNode<IActionStep | IDelayStep>
      }


export type JourneyState = {
    domain: DomainDto
    journey?: Journey
    mode: JourneyBuilderMode
    nodes: JourneyNode[]
    activeNode?: JourneyNode
    edges: JourneyEdge[]
    removedNodes: JourneyNode[]
    dependencies: {
        integrations?: Map<number, GetDomainIntegrationResponse>
    }
    copy: CopyState
    editorOpen: boolean
    editorFooter?: React.ReactNode
    closePopoversSignal: number
}

export type ActionDispatcher = (action: JourneyDispatchActionPack) => void
export type JourneyReducerElements = [context: JourneyState, dispatch: Dispatch<JourneyDispatchActionPack>]

export enum JourneyAction {
    SET_JOURNEY = 'setJourney',
    SET_ORIGINAL_STEPS = 'setOriginalSteps',
    UPDATE_ORIGINAL_STEPS = 'updateOriginalSteps',
    SET_ACTIVE_NODE = 'setActiveNode',
    ON_NODE_ADD = 'onNodeAdd',
    ON_NODE_UPDATE = 'onNodeUpdate',
    ON_NODE_REMOVE = 'onNodeRemove',
    ON_JOURNEY_CHANGES = 'onJourneyChanges',
    SET_JOURNEY_DEPENDENCIES = 'setJourneyDependencies',
    SET_JOURNEY_INTEGRATIONS = 'setJourneyIntegrationsDependencies',
    SET_COPY_STATE = 'setCopyState',
    SET_EDITOR_OPEN = 'setEditorOpen',
    SET_EDITOR_FOOTER = 'setEditorFooter',
    CLOSE_ALL_POPOVERS = 'closeAllPopovers',
    ON_PLACEHOLDER_REPLACE = 'onPlaceholderReplace',
    ON_NODE_STEP_CHANGE = 'onNodeStepChange',
    ON_NODE_ENRICH = 'onNodeEnrich',
}

export type JourneySetPack = {
    action: JourneyAction.SET_JOURNEY
    data: Journey
}

export type JourneyStepsSetPack = {
    action: JourneyAction.SET_ORIGINAL_STEPS
    data: IJourneyStep[]
}

export type JourneyStepsUpdatePack = {
    action: JourneyAction.UPDATE_ORIGINAL_STEPS
    data: IJourneyStep[]
}

export type JourneySetActiveNodePack = {
    action: JourneyAction.SET_ACTIVE_NODE
    data: JourneyNode | null
}

export type JourneyNodeAddPack = {
    action: JourneyAction.ON_NODE_ADD
    data: JourneyNode
}

export type JourneyNodeUpdatePack<T extends IJourneyStep = IJourneyStep> = {
    action: JourneyAction.ON_NODE_UPDATE
    data: { updates: JourneyNode<T> }
}

export type JourneyNodeRemovePack = {
    action: JourneyAction.ON_NODE_REMOVE
    data: JourneyNode
}

export type JourneyChangesPack = {
    action: JourneyAction.ON_JOURNEY_CHANGES
    data: { nodes?: JourneyNode[]; edges?: JourneyEdge[] }
}

export type JourneySetDependenciesPack = {
    action: JourneyAction.SET_JOURNEY_DEPENDENCIES
    data: JourneyState['dependencies']
}

export type JourneySetIntegrationsDependenciesPack = {
    action: JourneyAction.SET_JOURNEY_INTEGRATIONS
    data: JourneyState['dependencies']['integrations']
}

export type JourneyUpdateCopyPack = {
    action: JourneyAction.SET_COPY_STATE
    data: { type: CopyTypes; node: JourneyNode<IActionStep | IDelayStep> } | { node: null }
}

export type JourneySetEditorOpenPack = {
    action: JourneyAction.SET_EDITOR_OPEN
    data: boolean
}

export type JourneySetEditorFooterPack = {
    action: JourneyAction.SET_EDITOR_FOOTER
    data?: React.ReactNode
}

export type JourneyCloseAllPopoversPack = {
    action: JourneyAction.CLOSE_ALL_POPOVERS
}

export type JourneyPlaceholderReplacePack = {
    action: JourneyAction.ON_PLACEHOLDER_REPLACE
    data: { node: JourneyNode; stepType: AddStepType }
}

export type JourneyNodeStepChangePack = {
    action: JourneyAction.ON_NODE_STEP_CHANGE
    data: {
        nodeId: string
        step: IJourneyStep
        clearInvalid?: boolean
    }
}


export type JourneyNodeEnrichPack = {
    action: JourneyAction.ON_NODE_ENRICH
    data: {
        nodeId: string
        enrichedStep: IJourneyStep
    }
}

export type JourneyDispatchActionPack =
    | JourneySetPack
    | JourneySetActiveNodePack
    | JourneyNodeAddPack
    | JourneyNodeRemovePack
    | JourneyNodeUpdatePack
    | JourneyChangesPack
    | JourneyStepsSetPack
    | JourneyStepsUpdatePack
    | JourneySetDependenciesPack
    | JourneySetIntegrationsDependenciesPack
    | JourneyUpdateCopyPack
    | JourneySetEditorOpenPack
    | JourneySetEditorFooterPack
    | JourneyCloseAllPopoversPack
    | JourneyPlaceholderReplacePack
    | JourneyNodeStepChangePack
    | JourneyNodeEnrichPack

// endregion

// end region
